import React from "react";

export default function AntiSpam() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Anti-Spam Policy</h1>
        <p className="text-sm text-gray-500 mb-6">
          <strong>Last Updated:</strong> 4 April 2026<br />
          <strong>Trading Name:</strong> VSENDS<br />
          <strong>Legal Entity:</strong> V CAN RESOURCES LIMITED<br />
          <strong>Company Number:</strong> 13759348<br />
          <strong>Registered Office:</strong> 32 Abbey Drive, Dartford, England, DA2 7WP<br />
          <strong>Support Email:</strong> support@vsends.com<br />
          <strong>Compliance Email:</strong> vg@vcanresources.com
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          V CAN RESOURCES LIMITED ("we", "us", "our") is committed to maintaining a spam-free environment on the VSENDS platform. This Anti-Spam Policy sets out the rules and standards that all users must follow when sending communications through the Service.
        </p>
        <p className="text-gray-700 mb-4">
          Spam is one of the most significant threats to the usefulness and integrity of email communication. We have a zero-tolerance approach to spam and will take swift action against any user found to be sending unsolicited or abusive email through our platform.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">2. Definition of Spam</h2>
        <p className="text-gray-700 mb-4">
          For the purposes of this policy, "spam" means any unsolicited commercial email or bulk unsolicited messages sent through the Service. This includes, but is not limited to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Emails sent to recipients who have not explicitly consented to receive them.</li>
          <li>Messages sent to purchased, rented, scraped, or harvested email lists.</li>
          <li>Messages with misleading or falsified header information.</li>
          <li>Messages with deceptive subject lines that misrepresent the content.</li>
          <li>Chain letters, pyramid schemes, or other forms of unsolicited bulk messaging.</li>
          <li>Messages that do not include a clear and functioning unsubscribe mechanism.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">3. Consent Requirements</h2>
        <p className="text-gray-700 mb-4">
          All recipients of emails sent through the Service must have given their explicit, informed consent to receive communications from you. Consent must be:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Freely given:</strong> The recipient must have a genuine choice and control over whether to consent.</li>
          <li><strong>Specific:</strong> Consent must relate to specific, clearly defined purposes.</li>
          <li><strong>Informed:</strong> The recipient must know what they are consenting to.</li>
          <li><strong>Unambiguous:</strong> Consent must be given through a clear affirmative action (opt-in).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Pre-ticked boxes, silence, or inactivity do not constitute valid consent. You must be able to demonstrate how and when consent was obtained for each recipient on your mailing list.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">4. Prohibited List Practices</h2>
        <p className="text-gray-700 mb-4">
          The following list practices are strictly prohibited on the VSENDS platform:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Using purchased, rented, or shared email lists.</li>
          <li>Scraping email addresses from websites, social media, or other online sources.</li>
          <li>Using email addresses generated through dictionary attacks or random generation.</li>
          <li>Importing lists obtained from third parties without documented proof of consent.</li>
          <li>Adding individuals to your mailing list without their explicit permission.</li>
          <li>Using role-based email addresses (e.g., info@, admin@, sales@) without the consent of the responsible party.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">5. Email Content Requirements</h2>
        <p className="text-gray-700 mb-4">
          All emails sent through the Service must comply with the following content requirements:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Accurate headers:</strong> The "From", "To", and "Reply-To" fields must accurately identify the sender.</li>
          <li><strong>Truthful subject lines:</strong> Subject lines must not be misleading or deceptive about the content of the message.</li>
          <li><strong>Clear identification:</strong> Messages must clearly identify themselves as advertisements or promotional content where applicable.</li>
          <li><strong>Physical address:</strong> Messages must include a valid physical postal address of the sender.</li>
          <li><strong>Unsubscribe mechanism:</strong> Messages must include a clear, conspicuous, and functioning unsubscribe link.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">6. Unsubscribe Requirements</h2>
        <p className="text-gray-700 mb-4">
          Every email sent through the Service must include a functioning unsubscribe mechanism that:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Is clearly visible and easy to find within the email.</li>
          <li>Allows recipients to opt out with a single click or simple action.</li>
          <li>Processes unsubscribe requests within 10 business days.</li>
          <li>Does not require the recipient to provide additional information beyond their email address.</li>
          <li>Does not charge a fee or require the recipient to take any step other than sending a reply or visiting a single web page.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Once a recipient unsubscribes, you must not send them any further commercial emails. The VSENDS platform automatically manages unsubscribe requests and suppresses unsubscribed contacts from future campaigns.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">7. Compliance with Applicable Laws</h2>
        <p className="text-gray-700 mb-4">
          All users of the Service must comply with applicable anti-spam and email marketing laws, including but not limited to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>UK PECR:</strong> Privacy and Electronic Communications Regulations 2003.</li>
          <li><strong>UK GDPR:</strong> General Data Protection Regulation as incorporated into UK law.</li>
          <li><strong>CAN-SPAM Act:</strong> For emails sent to recipients in the United States.</li>
          <li><strong>CASL:</strong> Canada's Anti-Spam Legislation for emails sent to Canadian recipients.</li>
          <li><strong>Australia's Spam Act 2003:</strong> For emails sent to Australian recipients.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          It is your responsibility to ensure that your email marketing practices comply with all laws applicable to your recipients, regardless of their location.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">8. Monitoring and Enforcement</h2>
        <p className="text-gray-700 mb-4">
          We actively monitor email activity on the Service to detect and prevent spam. Our monitoring systems analyse:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Bounce rates and complaint rates for all campaigns.</li>
          <li>Spam trap hits and blacklist appearances.</li>
          <li>Unsubscribe rates and patterns.</li>
          <li>Content characteristics associated with spam.</li>
          <li>Sending patterns and volume anomalies.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          If we detect activity that may violate this policy, we may take the following actions:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Issue a warning and require remedial action.</li>
          <li>Temporarily suspend sending privileges pending investigation.</li>
          <li>Permanently terminate the Account and the Service.</li>
          <li>Report the activity to relevant law enforcement or regulatory authorities.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">9. Complaint Handling</h2>
        <p className="text-gray-700 mb-4">
          We take all spam complaints seriously. If you receive a complaint about emails sent through the Service, we will:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li>Investigate the complaint promptly and thoroughly.</li>
          <li>Request evidence of consent from the user responsible for the emails.</li>
          <li>Take appropriate enforcement action if a violation is confirmed.</li>
          <li>Notify the complainant of the outcome where appropriate.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          If you believe that you have received spam from a user of the Service, please forward the email to vg@vcanresources.com with full headers included. We will investigate and take appropriate action.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-3">10. Reporting Violations</h2>
        <p className="text-gray-700 mb-4">
          We encourage all users and recipients to report suspected spam or violations of this policy. Reports can be submitted to:
        </p>
        <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
          <li><strong>Email:</strong> vg@vcanresources.com</li>
          <li><strong>Support:</strong> support@vsends.com</li>
        </ul>
        <p className="text-gray-700 mb-4">
          When reporting a violation, please include the full email with headers, the sender's information, and a description of why you believe the email violates this policy. All reports will be treated confidentially and investigated promptly.
        </p>
        <p className="text-gray-700 mb-4">
          We reserve the right to update this Anti-Spam Policy at any time. Continued use of the Service following changes constitutes acceptance of the updated policy.
        </p>
      </main>
    </div>
  );
}
